import React from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Text,
  VStack,
  HStack,
  Icon,
} from '@chakra-ui/react';
import { WarningIcon } from '@chakra-ui/icons';
import { useIdleDetection } from '@/features/session';

/**
 * Formats milliseconds into MM:SS format.
 */
const formatTime = (ms: number): string => {
  const totalSeconds = Math.max(0, Math.floor(ms / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
};

/**
 * Modal that warns users about impending session timeout.
 */
export const IdleWarningModal: React.FC = () => {
  const { showWarning, timeRemaining, extendSession, logout, isLoggingOut } = useIdleDetection();
  const [isExtending, setIsExtending] = React.useState(false);

  const handleStaySignedIn = async () => {
    setIsExtending(true);
    try {
      await extendSession();
    } finally {
      setIsExtending(false);
    }
  };

  const handleLogout = async () => {
    await logout();
  };

  return (
    <Modal
      isOpen={showWarning}
      onClose={() => {}}
      closeOnOverlayClick={false}
      closeOnEsc={false}
      isCentered
      size="md"
    >
      <ModalOverlay />
      <ModalContent mx={4}>
        <ModalHeader>
          <HStack spacing={3}>
            <Icon as={WarningIcon} color="orange.500" boxSize={6} />
            <Text>Session Timeout Warning</Text>
          </HStack>
        </ModalHeader>

        <ModalBody>
          <VStack spacing={4} align="stretch">
            <Text fontSize="md">
              You&apos;ll be signed out in{' '}
              <Text as="span" fontWeight="bold" color="orange.500">
                {formatTime(timeRemaining)}
              </Text>{' '}
              due to inactivity.
            </Text>
            <Text fontSize="md">Would you like to stay signed in?</Text>
          </VStack>
        </ModalBody>

        <ModalFooter>
          <HStack spacing={3} width="100%" justify="flex-end">
            <Button variant="ghost" onClick={handleLogout} isDisabled={isExtending || isLoggingOut}>
              Logout
            </Button>
            <Button
              variant="primary"
              onClick={handleStaySignedIn}
              isLoading={isExtending}
              isDisabled={isLoggingOut}
              loadingText="Extending..."
            >
              Stay Signed In
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
